import { useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components/link/src/link.tsx";
import { Lozenge } from "@/libraries/design/components/lozenge/src/lozenge.tsx";
import wacToken from "@/libraries/design/foundations/token/src/wac-token.tsx";

import { useSetSelectedFeature } from "../context/hooks.ts";
import { RoadmapFeature } from "../types.ts";

import { gridTemplateColumns } from "./column-headers.tsx";
import { FeatureDescription } from "./feature-description.tsx";
import { StatusLozenge } from "./status-lozenge.tsx";

export interface CardProps {
    data: RoadmapFeature;
}

export const Card = (props: CardProps) => {
    const { data } = props;

    const intl = useIntl();
    const learnMoreLabel = intl.formatMessage({
        id: "roadmapFeatures.learnMore",
        defaultMessage: "Learn more"
    });

    const setSelectedFeature = useSetSelectedFeature();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = useCallback(() => {
        setSelectedFeature({
            slug: data.slug,
            pid: data.pid
        });
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "card",
            actionSubjectId: "roadmapFeatureCard",
            source: "roadmap",
            attributes: { label: data.title ?? "", slug: data.slug }
        }).fire();
    }, [
        setSelectedFeature,
        createAnalyticsEvent,
        data.slug,
        data.pid,
        data.title
    ]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleClick();
            }
        },
        [handleClick]
    );

    return (
        <div
            css={cardRowCss}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        >
            <div css={cellCss}>
                <h3 css={titleCss}>{data.title ?? ""}</h3>
                <div css={descriptionCss}>
                    <FeatureDescription content={data.description} />
                </div>
                <div>
                    <Link
                        text={learnMoreLabel}
                        aria-label={learnMoreLabel}
                        href={data.slug}
                        type="primary"
                        isStyleOnly={true}
                    />
                </div>
            </div>
            <div css={cellCss}>
                {data.category?.roadmapCategoryNameExternal ?? ""}
            </div>
            <div css={[cellCss, lozengeCellCss]}>
                {data.status?.roadmapStatusNameExternal && (
                    <StatusLozenge
                        text={data.status.roadmapStatusNameExternal}
                    />
                )}
            </div>
            <div css={cellCss}>{data.timeFrame ?? ""}</div>
            <div css={[cellCss, lozengeCellCss, appsCell]}>
                {(data.appsCollection?.items ?? []).map((app) => (
                    <Lozenge
                        key={app.filterQueryParam}
                        text={app.roadmapAppNameExternal}
                        appearance="neutral"
                    />
                ))}
            </div>
        </div>
    );
};

const cardRowCss = css({
    "width": "100%",
    "display": "grid",
    "gridTemplateColumns": gridTemplateColumns,
    "gridTemplateRows": "auto",
    "gap": `0px ${token("space.300")}`,
    "alignItems": "start",
    "justifyContent": "start",
    "padding": token("space.300"),
    "border": `1px solid ${wacToken("color.neutral.light400")}`,
    "backgroundColor": wacToken("color.neutral.light0"),
    "borderRadius": wacToken("corner.radius.card"),
    "cursor": "pointer",
    "boxSizing": "border-box",
    "transition": "box-shadow 150ms ease, transform 150ms ease",
    "&:hover": {
        transition: "box-shadow 125ms ease-out",
        boxShadow: token("elevation.shadow.overlay")
    },
    [media.below.md]: {
        display: "flex",
        flexDirection: "column",
        gap: token("space.150")
    }
});

const cellCss = css({
    "textAlign": "left",
    "@media (max-width: 900px)": {
        width: "100%"
    }
});

const titleCss = css({
    margin: "0",
    marginBlockEnd: token("space.150")
});

const descriptionCss = css({
    marginBlockEnd: token("space.150")
});

const lozengeCellCss = css({
    marginBlockStart: "2px"
});

const appsCell = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.050")
});
